import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, CustomSelect, RichTextEditor } from '@/components/ui';
import Input from '@/components/ui/Input/Input';
import FileUpload from '@/components/ui/FileUpload';
import Calendar from '@/components/ui/Calendar/Calendar';
import { Heading, Label, Paragraph } from '@/components/ui/Typography';
import { useCreateEvent, useEvent, useUpdateEvent } from '@/lib/api/hooks/event';
import { useFileUpload } from '@/lib/api/hooks/file-upload';
import { showToast } from '@/lib/toast';
import { ChevronLeftIcon } from '@/components/icons';
import TrashIcon from '@/assets/icons/TrashIcon';
import { Calendar1, FilesIcon, ImagesIcon } from 'lucide-react';
import { cn } from '@/utils/helpers/classnames';


const EventFormSchema = z
  .object({
    eventTitle: z.string().trim().min(1, 'Event title is required'),
    venueType: z.enum(['PHYSICAL', 'ONLINE'], { message: 'Venue type is required' }),
    eventVenue: z.string().trim().optional().or(z.literal('')),
    meetingLink: z.string().trim().optional().or(z.literal('')),
    additionalImageUrls: z.array(z.string().url()).optional(),
    additionalFileUrls: z.array(z.string().url()).optional(),
    startDate: z.string().min(1, 'Start date and time is required'),
    endDate: z.string(),
    description: z.string(),
    imageUrl: z.string().trim().min(1, 'Event image is required'),
  })
  .superRefine((data, ctx) => {
    const venueValue = (data.eventVenue || '').trim();
    const meetingLinkValue = (data.meetingLink || '').trim();

    if (data.venueType === 'PHYSICAL' && !venueValue) {
      ctx.addIssue({
        code: 'custom',
        path: ['eventVenue'],
        message: 'Event venue is required',
      });
    }

    if (data.venueType === 'ONLINE' && !meetingLinkValue) {
      ctx.addIssue({
        code: 'custom',
        path: ['meetingLink'],
        message: 'Virtual link is required',
      });
    }

    if (data.venueType === 'ONLINE' && meetingLinkValue && !looksLikeUrl(meetingLinkValue)) {
      ctx.addIssue({
        code: 'custom',
        path: ['meetingLink'],
        message: 'Enter a valid URL',
      });
    }
  });

type EventFormData = z.infer<typeof EventFormSchema>;

const DEFAULT_VALUES: EventFormData = {
  eventTitle: '',
  venueType: 'PHYSICAL',
  eventVenue: '',
  meetingLink: '',
  additionalImageUrls: [],
  additionalFileUrls: [],
  startDate: '',
  endDate: '',
  description: '',
  imageUrl: '',
};

const VENUE_TYPE_OPTIONS = [
  { value: 'PHYSICAL', label: 'Physical' },
  { value: 'ONLINE', label: 'Online' },
];

const formatDateForApi = (dateValue: string): string => {
  if (!dateValue) {
    return '';
  }

  if (dateValue.length === 16) {
    return `${dateValue}:00`;
  }

  return dateValue.slice(0, 19);
};

const looksLikeUrl = (value: string): boolean => /^https?:\/\//i.test(value.trim());

const getFileNameFromUrl = (url: string): string => {
  const cleaned = url.split('?')[0].split('#')[0];
  const name = cleaned.split('/').pop();
  return name && name.trim() ? decodeURIComponent(name) : 'uploaded-file';
};

const toDateTimeLocal = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const getLocalFileKey = (file: File): string => `${file.name}-${file.size}-${file.lastModified}`;

const EventEditor: React.FC = () => {
  const { eventId } = useParams<{ eventId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const isEditMode = Boolean(eventId);
  const existingEvent = location.state?.event;

  const { data: eventData } = useEvent(eventId);
  const createMutation = useCreateEvent();
  const updateMutation = useUpdateEvent();
  const fileUploadMutation = useFileUpload();

  const [imageUrl, setImageUrl] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadKey, setUploadKey] = useState(0);
  const [additionalImageUploadKey, setAdditionalImageUploadKey] = useState(0);
  const [additionalFileUploadKey, setAdditionalFileUploadKey] = useState(0);
  const [additionalImageFiles, setAdditionalImageFiles] = useState<File[]>([]);
  const [additionalFileFiles, setAdditionalFileFiles] = useState<File[]>([]);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isUploadingAdditionalAssets, setIsUploadingAdditionalAssets] = useState(false);
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<EventFormData>({
    resolver: zodResolver(EventFormSchema),
    defaultValues: DEFAULT_VALUES,
    mode: 'all',
  });

  const [watchTitle, watchVenueType, watchAdditionalImageUrls, watchAdditionalFileUrls] = useWatch({
    control,
    name: ['eventTitle', 'venueType', 'additionalImageUrls', 'additionalFileUrls'],
  });

  useEffect(() => {
    if (!(isEditMode && (eventData?.data || existingEvent))) {
      return;
    }

    const event = eventData?.data || existingEvent;
    const rawVenue = event.eventVenue || '';
    const meetingLink = event.meetingLink || '';
    const isOnlineVenue = Boolean(meetingLink) || looksLikeUrl(rawVenue);

    reset({
      eventTitle: event.eventTitle || '',
      eventVenue: isOnlineVenue ? '' : rawVenue,
      meetingLink: isOnlineVenue ? (meetingLink || rawVenue) : '',
      venueType: isOnlineVenue ? 'ONLINE' : 'PHYSICAL',
      additionalImageUrls: event.additionalImageUrls || [],
      additionalFileUrls: event.additionalFileUrls || [],
      startDate: event.startDate ? String(event.startDate).slice(0, 16) : '',
      endDate: event.endDate ? String(event.endDate).slice(0, 16) : '',
      description: event.description || '',
      imageUrl: event.imageUrl || '',
    });

    setImageUrl(event.imageUrl || '');
    setImagePreview(event.imageUrl || '');
    setImageFile(null);
    setAdditionalImageFiles([]);
    setAdditionalFileFiles([]);
    setUploadKey((prev) => prev + 1);
    setAdditionalImageUploadKey((prev) => prev + 1);
    setAdditionalFileUploadKey((prev) => prev + 1);
  }, [isEditMode, eventData, existingEvent, reset]);

  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const isBusy = isSubmitting || isDraftSaving || isPublishing || isUploadingImage || isUploadingAdditionalAssets;

  useEffect(() => {
    if (watchVenueType === 'ONLINE') {
      setValue('eventVenue', '', { shouldValidate: true });
      return;
    }

    setValue('meetingLink', '', { shouldValidate: true });
  }, [watchVenueType, setValue]);

  const handleImageFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      showToast.error('Please upload a valid image file.');
      return;
    }

    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }

    const objectUrl = URL.createObjectURL(file);
    setImageFile(file);
    setImagePreview(objectUrl);
    setValue('imageUrl', objectUrl, { shouldValidate: true });
    setUploadKey((prev) => prev + 1);
  };

  const handleImageFilesChange = (files: File[] | null) => {
    if (!files || files.length === 0) {
      return;
    }

    handleImageFile(files[0]);
  };

  const uploadAdditionalFiles = async (
    files: File[],
    category: 'IMAGE' | 'DOCUMENT',
    onFileUploaded?: (file: File) => void
  ) => {
    const uploadedUrls: string[] = [];

    for (const file of files) {
      const uploadResult = await fileUploadMutation.mutateAsync({ file, category });

      if (!uploadResult.success || !uploadResult.data?.fileUrl) {
        throw new Error(`Failed to upload ${file.name}`);
      }

      uploadedUrls.push(uploadResult.data.fileUrl);
      onFileUploaded?.(file);
    }

    return uploadedUrls;
  };

  const handleAdditionalImagesChange = async (files: File[] | null) => {
    if (!files || files.length === 0) return;

    setAdditionalImageFiles((prev) => {
      const merged = [...prev, ...files];
      const uniqueByKey = new Map(merged.map((file) => [getLocalFileKey(file), file]));
      return Array.from(uniqueByKey.values());
    });
    setAdditionalImageUploadKey((prev) => prev + 1);
  };

  const handleAdditionalFilesChange = async (files: File[] | null) => {
    if (!files || files.length === 0) return;

    setAdditionalFileFiles((prev) => {
      const merged = [...prev, ...files];
      const uniqueByKey = new Map(merged.map((file) => [getLocalFileKey(file), file]));
      return Array.from(uniqueByKey.values());
    });
    setAdditionalFileUploadKey((prev) => prev + 1);
  };

  const removeAdditionalImageUrl = (url: string) => {
    const currentUrls = getValues('additionalImageUrls') || [];
    setValue(
      'additionalImageUrls',
      currentUrls.filter((value) => value !== url),
      { shouldDirty: true, shouldValidate: true }
    );
  };

  const removeAdditionalFileUrl = (url: string) => {
    const currentUrls = getValues('additionalFileUrls') || [];
    setValue(
      'additionalFileUrls',
      currentUrls.filter((value) => value !== url),
      { shouldDirty: true, shouldValidate: true }
    );
  };

  const removePendingAdditionalImage = (fileKey: string) => {
    setAdditionalImageFiles((prev) => prev.filter((file) => getLocalFileKey(file) !== fileKey));
  };

  const removePendingAdditionalFile = (fileKey: string) => {
    setAdditionalFileFiles((prev) => prev.filter((file) => getLocalFileKey(file) !== fileKey));
  };

  const handleRemoveLocalPreview = () => {
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }

    setImageFile(null);
    setImagePreview(imageUrl || '');
    setValue('imageUrl', imageUrl || '', { shouldValidate: true });
    setUploadKey((prev) => prev + 1);
  };

  const submitWithStatus = async (formData: EventFormData, status: 'DRAFT' | 'PUBLISH') => {
    const setLoading = status === 'DRAFT' ? setIsDraftSaving : setIsPublishing;
    setLoading(true);

    try {
      let finalImageUrl = imageUrl;

      if (imageFile) {
        setIsUploadingImage(true);
        const uploadResult = await fileUploadMutation.mutateAsync({ file: imageFile, category: 'IMAGE' });

        if (uploadResult.success && uploadResult.data?.fileUrl) {
          finalImageUrl = uploadResult.data.fileUrl;
          setImageUrl(uploadResult.data.fileUrl);
          setValue('imageUrl', uploadResult.data.fileUrl, { shouldValidate: true });
        } else {
          throw new Error('Image upload failed');
        }

        setIsUploadingImage(false);
      }

      setIsUploadingAdditionalAssets(true);
      const uploadedAdditionalImageUrls = additionalImageFiles.length
        ? await uploadAdditionalFiles(additionalImageFiles, 'IMAGE', (file) => {
            const uploadedKey = getLocalFileKey(file);
            setAdditionalImageFiles((prev) => prev.filter((item) => getLocalFileKey(item) !== uploadedKey));
          })
        : [];
      const uploadedAdditionalFileUrls = additionalFileFiles.length
        ? await uploadAdditionalFiles(additionalFileFiles, 'DOCUMENT', (file) => {
            const uploadedKey = getLocalFileKey(file);
            setAdditionalFileFiles((prev) => prev.filter((item) => getLocalFileKey(item) !== uploadedKey));
          })
        : [];

      const finalAdditionalImageUrls = Array.from(new Set([...(formData.additionalImageUrls || []), ...uploadedAdditionalImageUrls]));
      const finalAdditionalFileUrls = Array.from(new Set([...(formData.additionalFileUrls || []), ...uploadedAdditionalFileUrls]));

      setValue('additionalImageUrls', finalAdditionalImageUrls, { shouldDirty: true, shouldValidate: true });
      setValue('additionalFileUrls', finalAdditionalFileUrls, { shouldDirty: true, shouldValidate: true });

  

      const payload = {
        eventTitle: formData.eventTitle,
        eventVenue: formData.venueType === 'PHYSICAL' ? (formData.eventVenue ?? '') : '',
        meetingLink: formData.venueType === 'ONLINE' ? ((formData.meetingLink || '').trim() || null) : null,
        additionalImageUrls: finalAdditionalImageUrls,
        additionalFileUrls: finalAdditionalFileUrls,
        startDate: formatDateForApi(formData.startDate),
        imageUrl: finalImageUrl || '',
        description: formData.description || "",
        status,
      } as EventFormData & { status: 'DRAFT' | 'PUBLISH' };

          if(formData.endDate) {
        payload.endDate = formatDateForApi(formData.endDate);
      }

      if (isEditMode && eventId) {
        const result = await updateMutation.mutateAsync({
          eventId,
          data: payload,
        });

        if (!result.success) {
          throw new Error('Update failed');
        }

        showToast.success(status === 'PUBLISH' ? 'Event published successfully' : 'Event saved as draft successfully');
      } else {
        const result = await createMutation.mutateAsync(payload);

        if (!result.success) {
          throw new Error('Creation failed');
        }

        showToast.success(status === 'PUBLISH' ? 'Event published successfully' : 'Event created as draft successfully');
      }

      navigate('/admin/events');
      setAdditionalImageFiles([]);
      setAdditionalFileFiles([]);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save event';
      showToast.error(message);
      setIsUploadingImage(false);
    } finally {
      setIsUploadingAdditionalAssets(false);
      setLoading(false);
    }
  };

  return (
    <div className='rounded-xl bg-white relative space-y-6'>
      <header className='border-b border-gray-200 bg-white sticky top-0 z-10 px-4 py-4 md:px-7'>
        <div className='flex items-center justify-between'>
          <button
            onClick={() => navigate('/admin/events')}
            disabled={isBusy}
            className='flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900 disabled:opacity-50'
          >
            <ChevronLeftIcon className='h-5 w-5' />
            Go back
          </button>

          <div className='flex items-center gap-3'>
            <Button
              type='button'
              variant='secondary'
              
              loading={isBusy}
              onClick={() => {
                // void submitWithStatus(getValues(), 'DRAFT');
                   void handleSubmit((data) => submitWithStatus(data, 'DRAFT'))();
              }}
            >
              Save as Draft
            </Button>
            <Button
              type='button'
              
              loading={isBusy}
              onClick={() => {
                void handleSubmit((data) => submitWithStatus(data, 'PUBLISH'))();
              }}
            >
              Publish
            </Button>
          </div>
        </div>
      </header>

      <div className='py-4 max-w-5xl mx-auto'>
        <h1 className='mb-3 text-2xl font-bold text-gray-900'>{isEditMode ? 'Edit Event' : 'Create Event'}</h1>
        <nav className='flex items-center gap-2 text-sm'>
          <button
            onClick={() => navigate('/admin/events')}
            className='text-wine-100 hover:text-wine-200 hover:underline disabled:opacity-50 disabled:cursor-not-allowed'
            disabled={isBusy}
          >
            All Events
          </button>
          {isEditMode && (
            <>
              <span className='text-gray-400'>/</span>
              <button
                onClick={() => navigate(`/admin/events/${eventId}`)}
                className='text-wine-100 hover:text-wine-200 hover:underline disabled:opacity-50 disabled:cursor-not-allowed'
                disabled={isBusy}
              >
                {watchTitle || 'Event'}
              </button>
            </>
          )}
          <span className='text-gray-400'>/</span>
          <span className='text-gray-600'>{isEditMode ? 'Edit Event' : 'Create Event'}</span>
        </nav>
      </div>

      <div className='max-w-5xl mx-auto space-y-6 '>
        <form onSubmit={(event) => event.preventDefault()} className='space-y-6'>
          <div className='rounded-xl border border-gray-200 bg-white p-4 md:p-5'>
            <Heading level={3} className={cn('text-gray-900', 'after:content-["*"] after:ml-0.5 after:text-red-500')}>
              Event Image
            </Heading>
           

            <div className='mt-4 h-72 grid gap-4 md:grid-cols-2 lg:grid-cols-[1fr_2fr]'>
              <div className='flex flex-col gap-4'>
                 <Paragraph className='mt-1 text-sm '>
              Upload a PNG or JPEG banner image for this event.
            </Paragraph>
             
              <FileUpload
                key={uploadKey}
                onChange={handleImageFilesChange}
                disabled={isBusy}
                allowedFileTypes={['image/*']}
                $maxSizeMB={10}
                required
                placeholder=''
                error={errors.imageUrl?.message}
                 showPreview={false}
                containerClassName='h-52 flex flex-col items-center gap-4 text-center bg-gray-50 border-gray-200'
              >
                <Paragraph >Drag file to upload</Paragraph>
                <Paragraph className='text-xs '>
                  Supported format: PNG, JPEG. Maximum size: 10MB.
                </Paragraph>
                <div className='flex justify-center'>
                  <Button type='button' variant='secondary' >
                    Browse File
                  </Button>
                </div>
              </FileUpload>
 </div>

              <div className='relative h-full overflow-hidden rounded-lg border border-gray-200 bg-gray-50'>
                {imagePreview ? (
                  <img src={imagePreview} alt='Event preview' className='h-full w-full object-cover' />
                ) : (
                  <div className='flex h-full items-center justify-center px-4 text-center text-sm '>
                    Image preview appears here.
                  </div>
                )}

                {imageFile ? (
                  <button
                    type='button'
                    onClick={handleRemoveLocalPreview}
                    className='absolute right-2 top-2 rounded-md bg-white/90 p-2 hover:bg-white'
                    aria-label='Remove local uploaded image'
                  >
                    <TrashIcon fill='#DE534E' className='h-8 w-8' />
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          <div className='rounded-xl border border-gray-200 bg-white p-4 md:p-5'>
            <div className='space-y-6'>
              <Controller
                name='eventTitle'
                control={control}
                render={({ field }) => (
                  <Input
                    label='Event Title'
                    required
                    placeholder='Eg. Annual Seminar 001'
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.eventTitle?.message}
                    disabled={isBusy}
                    $marginbottom='0'
                    
                  />
                )}
              />

              <div className='grid gap-4 md:grid-cols-2 items-end'>
                <Controller
                  name='venueType'
                  control={control}
                  render={({ field }) => (
                    <div className=''>
                  
                      <CustomSelect
                        id='venueType'
                        label='Venue Type'
                        name={field.name}
                        value={field.value}
                        onChange={(value) => field.onChange(Array.isArray(value) ? value[0] ?? '' : value)}
                        onBlur={() => field.onBlur()}
                        options={VENUE_TYPE_OPTIONS}
                        placeholder='Select venue type'
                        disabled={isBusy}
                        error={errors.venueType?.message}
                      />
                
                    </div>
                  )}
                />

                <Controller
                  name={watchVenueType === 'ONLINE' ? 'meetingLink' : 'eventVenue'}
                  control={control}
                  render={({ field }) => (
                    <Input
                      label={watchVenueType === 'ONLINE' ? 'Virtual Link' : 'Event Venue'}
                      required
                      placeholder={
                        watchVenueType === 'ONLINE'
                          ? 'https://example.com/meeting-link'
                          : 'Eg. Abuja International Conference Center'
                      }
                      value={field.value}
                      onChange={field.onChange}
                      error={watchVenueType === 'ONLINE' ? errors.meetingLink?.message : errors.eventVenue?.message}
                      disabled={isBusy}
                      $marginbottom='0'
                      
                    />
                  )}
                />
              </div>
            </div>
          </div>

          <div className='rounded-xl border border-gray-200 bg-white p-4 md:p-5'>
            <div className='grid gap-4 md:grid-cols-2'>
              <Controller
                name='startDate'
                control={control}
                render={({ field }) => (
                  <Calendar
                    label='Start Date & Time'
                    required
                    selectedDate={field.value ? new Date(field.value) : null}
                    onChange={(date) => {
                      if (date instanceof Date) {
                        field.onChange(toDateTimeLocal(date));
                      } else {
                        field.onChange('');
                      }
                    }}
                    showTimeSelect
                    error={errors.startDate?.message}
                    $isDisabled={isBusy}
                    $marginbottom='0'
                    icon={<Calendar1 />}
                    // minDate={new Date()}
                  />
                )}
              />

              <Controller
                name='endDate'
                control={control}
                render={({ field }) => (
                  <Calendar
                    label='End Date & Time'
                    selectedDate={field.value ? new Date(field.value) : null}
                    onChange={(date) => {
                      if (date instanceof Date) {
                        field.onChange(toDateTimeLocal(date));
                      } else {
                        field.onChange('');
                      }
                    }}
                    showTimeSelect
                    error={errors.endDate?.message}
                    $isDisabled={isBusy}
                    $marginbottom='0'
                    icon={<Calendar1 />}
                    // minDate={new Date()}
                  />
                )}
              />
            </div>
          </div>

          <div className='rounded-xl border border-gray-200 bg-white p-4 md:p-5'>
            <Label className='mb-2 block'>Description</Label>
            <Controller
              name='description'
              control={control}
              render={({ field }) => (
                <RichTextEditor
                  value={field.value || ''}
                  onChange={field.onChange}
                  disabled={isBusy}
                  placeholder='Type here....'
                  className='border-gray-200'
                  toolbarContainerClassName='bg-gray-50 border-gray-200 px-4 py-2'
                  contentAreaClassName='min-h-[220px] max-h-[460px] p-3'
                />
              )}
            />

            <div className='mt-6 grid gap-4 md:grid-cols-2'>
              <div>
                <Label className='mb-2 block'>Additional Images</Label>
                <FileUpload
                  key={additionalImageUploadKey}
                  onChange={handleAdditionalImagesChange}
                  disabled={isBusy}
                  allowedFileTypes={['image/*']}
                  multiple
                  showPreview={false}
                  $maxSizeMB={10}
                  placeholder=''
                  containerClassName='min-h-32 flex flex-col items-center justify-center gap-2 text-center bg-gray-50 border-gray-200'
                >
                  <ImagesIcon className='h-12 w-12 ' strokeWidth={1.25} />
                  <Paragraph>Drag images to upload</Paragraph>
                  <Paragraph className='!text-sm text-gray-500'>PNG, JPG, JPEG, WEBP. Max 10MB each.</Paragraph>
                </FileUpload>

                {(watchAdditionalImageUrls || []).length > 0 && (
                  <div className='mt-3 flex flex-wrap gap-2'>
                    {(watchAdditionalImageUrls || []).map((url) => (
                      <div key={url} className='inline-flex max-w-full items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5'>
                        <span className='truncate text-sm text-gray-700' title={url}>{getFileNameFromUrl(url)}</span>
                        <button
                          type='button'
                          className='shrink-0 rounded-full border border-[#E6BFC1] px-2 py-0.5 text-xs font-semibold text-[#A8393D] hover:bg-[#FFF5F6]'
                          onClick={() => removeAdditionalImageUrl(url)}
                          aria-label='Remove additional image'
                        >
                          x
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {additionalImageFiles.length > 0 && (
                  <div className='mt-3 flex flex-wrap gap-2'>
                    {additionalImageFiles.map((file) => {
                      const fileKey = getLocalFileKey(file);
                      return (
                        <div key={fileKey} className='inline-flex max-w-full items-center gap-2 rounded-full border border-[#9BC3AB] bg-[#F3F9F5] px-3 py-1.5'>
                          <span className='truncate text-sm text-gray-700' title={file.name}>{file.name}</span>
                          <span className='rounded-full bg-white px-1.5 py-0.5 text-[10px] font-semibold text-[#0D6B3C]'>Pending</span>
                          <button
                            type='button'
                            className='shrink-0 rounded-full border border-[#E6BFC1] px-2 py-0.5 text-xs font-semibold text-[#A8393D] hover:bg-[#FFF5F6]'
                            onClick={() => removePendingAdditionalImage(fileKey)}
                            aria-label='Remove pending additional image'
                          >
                            x
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div>
                <Label className='mb-2 block'>Additional Files</Label>
                <FileUpload
                  key={additionalFileUploadKey}
                  onChange={handleAdditionalFilesChange}
                  disabled={isBusy}
                  multiple
                  showPreview={false}
                  $maxSizeMB={50}
                  placeholder=''
                  containerClassName='min-h-32 flex flex-col items-center justify-center gap-2 text-center bg-gray-50 border-gray-200'
                >
                  <FilesIcon className='h-12 w-12 ' strokeWidth={1.25} />
                  <Paragraph>Drag documents to upload</Paragraph>
                  <Paragraph className='!text-sm '>PDF, DOCX, XLSX, PPTX. Max 50MB each.</Paragraph>
                </FileUpload>

                {(watchAdditionalFileUrls || []).length > 0 && (
                  <div className='mt-3 flex flex-wrap gap-2'>
                    {(watchAdditionalFileUrls || []).map((url) => (
                      <div key={url} className='inline-flex max-w-full items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5'>
                        <span className='truncate text-sm text-gray-700' title={url}>{getFileNameFromUrl(url)}</span>
                        <button
                          type='button'
                          className='shrink-0 rounded-full border border-[#E6BFC1] px-2 py-0.5 text-xs font-semibold text-[#A8393D] hover:bg-[#FFF5F6]'
                          onClick={() => removeAdditionalFileUrl(url)}
                          aria-label='Remove additional file'
                        >
                          x
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {additionalFileFiles.length > 0 && (
                  <div className='mt-3 flex flex-wrap gap-2'>
                    {additionalFileFiles.map((file) => {
                      const fileKey = getLocalFileKey(file);
                      return (
                        <div key={fileKey} className='inline-flex max-w-full items-center gap-2 rounded-full border border-[#9BC3AB] bg-[#F3F9F5] px-3 py-1.5'>
                          <span className='truncate text-sm text-gray-700' title={file.name}>{file.name}</span>
                          <span className='rounded-full bg-white px-1.5 py-0.5 text-[10px] font-semibold text-[#0D6B3C]'>Pending</span>
                          <button
                            type='button'
                            className='shrink-0 rounded-full border border-[#E6BFC1] px-2 py-0.5 text-xs font-semibold text-[#A8393D] hover:bg-[#FFF5F6]'
                            onClick={() => removePendingAdditionalFile(fileKey)}
                            aria-label='Remove pending additional file'
                          >
                            x
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            {/* <Controller
              name='imageUrl'
              control={control}
              render={({ field }) => <input type='hidden' value={field.value || ''} onChange={field.onChange} />}
            /> */}
           
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventEditor;
